import { createSearchParams, generatePath } from "react-router-dom";
import { pagePaths } from "../constants/pagePaths";

export const buildSignUpPageUrl = () => {
  return pagePaths.signUp;
};

export const buildHomeLoginPageUrl = () => {
  return pagePaths.home;
};

export const buildUserNotFoundUrl = (emailAddress: string | undefined) => {
  const searchParameters = emailAddress !== undefined ? createSearchParams({ emailAddress }) : undefined;
  const url = pagePaths.userNotFound;
  if (searchParameters) {
    return `${url}/?${searchParameters}`;
  }
  return url;
};

export const buildUserDisabledUrl = (emailAddress: string | undefined) => {
  const searchParameters = emailAddress !== undefined ? createSearchParams({ emailAddress }) : undefined;
  const url = pagePaths.userDisabled;
  if (searchParameters) {
    return `${url}/?${searchParameters}`;
  }
  return url;
};

export const buildVerifyEmailAddressPageUrl = ({ emailAddress }: { emailAddress: string }) => {
  const searchParameters = createSearchParams({ emailAddress });
  return `${pagePaths.verifyEmailAddress}/?${searchParameters}`;
};

export const buildSignInPageUrl = ({
  email,
  password,
}: {
  email?: string;
  password?: string;
} = {}) => {
  if (email && password) {
    const searchParameters = createSearchParams({ email, password });
    return `${pagePaths.signIn}/?${searchParameters}`;
  }
  if (email) {
    const searchParameters = createSearchParams({ email });
    return `${pagePaths.signIn}/?${searchParameters}`;
  }
  return pagePaths.signIn;
};

export const buildFederatedSignInRetryUrl = (logFederationUser: string) => {
  const searchParameters = createSearchParams({ logFederationUser });
  const url = `${pagePaths.signIn}/?${searchParameters}`;
  return url;
};

export const buildDistributorSignInPageUrl = () => {
  return pagePaths.distributorSignIn;
};

export const buildResetPasswordPageUrl = ({ emailAddress }: { emailAddress?: string }) => {
  if (emailAddress) {
    const searchParameters = createSearchParams({ emailAddress });
    return `${pagePaths.resetPassword}/?${searchParameters}`;
  }
  return pagePaths.resetPassword;
};

export const buildChangePasswordPageUrl = ({ emailAddress }: { emailAddress: string }) => {
  const searchParameters = createSearchParams({ emailAddress });
  return `${pagePaths.changePassword}/?${searchParameters}`;
};

export const buildClassroomsPageUrl = () => {
  return pagePaths.classrooms;
};

export const buildExamSupportPageUrl = () => {
  return pagePaths.examSupport;
};

export const buildClassroomPageUrl = (classroomId: string) => {
  return generatePath(`${pagePaths.classroomDetail}`, { classroomId: classroomId });
};

export const buildClassroomMembersPageUrl = (classroomId: string) => {
  return generatePath(`${pagePaths.classroomDetail}` + `/${pagePaths.classroomDetailPages.members}`, { classroomId: classroomId });
};

export const buildClassroomPageMemberReportUrl = (userId: string) => {
  return generatePath(`${pagePaths.user.report}`, { userId: userId });
};

export const buildClassroomPageMemberActivitiesUrl = (userId: string) => {
  return generatePath(`${pagePaths.user.activities}`, { userId: userId });
};

export const buildClassroomPageMemberAnalyticsUrl = (userId: string) => {
  return generatePath(`${pagePaths.user.analytics}`, { userId: userId });
};

export const buildClassroomPageMemberSettingsUrl = (userId: string) => {
  return generatePath(`${pagePaths.user.settings}`, { userId: userId });
};

export const buildMemberActivitiesPracticeExercisesUrl = () => {
  return generatePath(`${pagePaths.userActivities.practiceExercises}`);
};

export const buildMemberActivitiesPracticeTestsUrl = () => {
  return generatePath(`${pagePaths.userActivities.practiceTests}`);
};

export const buildMemberActivitiesMockExamsUrl = () => {
  return generatePath(`${pagePaths.userActivities.mockExams}`);
};

export const buildMemberActivitiesExamSupportUrl = () => {
  return generatePath(`${pagePaths.userActivities.examSupport}`);
};

export const buildMemberActivitiesAssignmentsUrl = () => {
  return generatePath(`${pagePaths.userActivities.assignments}`);
};

export const buildPracticeTestPageUrl = (practiceTestId: string, userId: string) => {
  return generatePath(`${pagePaths.practiceTestPage}`, { practiceTestId: practiceTestId, userId: userId });
};

export const buildMockExamAllSectionsPageUrl = (level: string, number: number, userId: string) => {
  return generatePath(`${pagePaths.mockExamPage}`, { level: level, number: number, userId: userId });
};

export const buildMockExamSectionDetailPageUrl = (level: string, number: number, section: string, userId: string) => {
  return generatePath(`${pagePaths.mockExamSectionPage}`, { level: level, number: number, section: section, userId: userId });
};

export const buildBlockExercisesUrl = (id: string, pageType: string) => {
  if (pageType === "Classroom") {
    return generatePath(`${pagePaths.classroomBlockExercises}`, { resourceId: id });
  }
  return generatePath(`${pagePaths.studentBlockExercises}`, { resourceId: id });
};

export const buildClassroomAddMembersUrl = (classroomId: string) => {
  return generatePath(`${pagePaths.classroomAddMembers}`, { classroomId: classroomId });
};

export const buildExercisePageUrl = (level: string, section: string, part: string, exerciseNumber: number) => {
  return generatePath(`${pagePaths.exercisePage}`, { level: level, section: section, part: part, number: exerciseNumber });
};

export const buildLevelPageUrl = (level: string) => {
  return generatePath(`${pagePaths.levelPage}`, { level: level });
};

export const buildTeachersPageUrl = () => {
  return pagePaths.teachers;
};

export const buildStudentsPageUrl = () => {
  return pagePaths.students;
};

export const buildHomePageUrl = () => {
  return pagePaths.home;
};

export const buildSchoolPageUrl = () => {
  return pagePaths.schoolPage;
};

export const buildCorrectionsPendingPageUrl = () => {
  return pagePaths.correcctionsPending;
};

export const buildMyAccountPageUrl = () => {
  return pagePaths.myAccount;
};

export const buildCheckoutPageUrl = () => {
  return pagePaths.checkout;
};

export const buildDistributorCheckoutPageUrl = () => {
  return `${pagePaths.distributor}/${pagePaths.distributorPages.checkout}`;
};

export const buildDistributorUserAccountPageUrl = () => {
  return `${pagePaths.distributor}/${pagePaths.distributorPages.myAccount}`;
};

export const buildMyLicensesPageUrl = () => {
  return pagePaths.myLicenses;
};

export const buildMyPaymentsPageUrl = () => {
  return pagePaths.myPayments;
};

export const buildApplyLicensesPageUrl = () => {
  return pagePaths.applyLicenses;
};

export const buildDistributorApplyLicensesPageUrl = () => {
  return `${pagePaths.distributor}/${pagePaths.distributorPages.assignLicenses}`;
};

export const buildTeamsPageUrl = () => {
  return `${pagePaths.distributor}/${pagePaths.distributorPages.teams}`;
};

export const buildLicensesPaymentsPageUrl = () => {
  return `${pagePaths.distributor}/${pagePaths.distributorPages.licensesPayments}`;
};

export const buildClientsPageUrl = () => {
  return `${pagePaths.distributor}/${pagePaths.distributorPages.clients}`;
};

export const buildOrdersPageUrl = () => {
  return `${pagePaths.distributor}/${pagePaths.distributorPages.orders}`;
};

export const buildAnalyticsPageUrl = () => {
  return `${pagePaths.distributor}/${pagePaths.distributorPages.analytics}`;
};

export const buildCompanyInfoPageUrl = () => {
  return `${pagePaths.distributor}/${pagePaths.distributorPages.companyInfo}`;
};

export const buildLicensesPageUrl = () => {
  return `${pagePaths.distributor}/${pagePaths.distributorPages.licenses}`;
};

export const buildUserReportPage = (userId: string) => {
  return generatePath(`${pagePaths.userData}/${pagePaths.user.report}`, { userId: userId });
};

export const buildUserActivitiesPage = (userId: string) => {
  return generatePath(`${pagePaths.userData}/${pagePaths.user.activities}/${pagePaths.userActivities.practiceExercises}`, { userId: userId });
};

export const buildProfilePageUrl = (userId: string) => {
  return generatePath(`${pagePaths.myProfile}/${pagePaths.user.activities}/${pagePaths.userActivities.practiceExercises}`, { userId: userId });
};

export const buildSupportPageUrl = () => {
  return pagePaths.support;
};
